/**
 * SCORM 2004 adapter — bridges scorm-again's Scorm2004API with our
 * backend CMI persistence (commit/restore via Session JWT).
 *
 * Usage:
 *   const adapter = new Scorm2004Adapter(config);
 *   adapter.initialize();         // sets up scorm-again + listeners
 *   adapter.loadIntoFrame(iframe); // injects API into iframe
 *   adapter.destroy();            // cleanup
 */

import type {
    ScormAdapterConfig,
    ScormCommitResult,
    ScormCompletionEvent,
} from './types';
import { commitCmi } from './api';

// scorm-again is imported dynamically to avoid SSR issues
let Scorm2004API: any = null;

async function loadScorm2004API() {
    if (!Scorm2004API) {
        const mod = await import('scorm-again');
        Scorm2004API = (mod as any).Scorm2004API ?? (mod as any).default?.Scorm2004API;
    }
    return Scorm2004API;
}

export class Scorm2004Adapter {
    private config: ScormAdapterConfig;
    private api: any = null;
    private commitTimer: ReturnType<typeof setInterval> | null = null;
    private destroyed = false;

    constructor(config: ScormAdapterConfig) {
        this.config = {
            autoCommitIntervalMs: 30_000,
            ...config,
        };
    }

    /**
     * Initialize the scorm-again SCORM 2004 API instance
     * and set up event listeners for commit/completion.
     */
    async initialize(): Promise<void> {
        const API = await loadScorm2004API();

        this.api = new API({
            autocommit: false, // We handle commit ourselves
            autocommitSeconds: 0,
            logLevel: 1, // Errors only
            selfReportSessionTime: true,
            lmsCommitUrl: '', // We override commit manually
        });

        // Pre-populate CMI data if resuming a session
        if (this.config.initialCmiData && Object.keys(this.config.initialCmiData).length > 0) {
            this.api.loadFromJSON(this.config.initialCmiData);
        }

        // Set learner info
        if (this.config.learnerId) {
            this.api.setCMIValue('cmi.learner_id', this.config.learnerId);
        }
        if (this.config.learnerName) {
            this.api.setCMIValue('cmi.learner_name', this.config.learnerName);
        }

        // Listen for Commit
        this.api.on('Commit', () => {
            this.handleCommit();
        });

        // Listen for completion_status changes
        this.api.on('SetValue.cmi.completion_status', (_: string, value: string) => {
            if (value === 'completed') {
                this.handleCompletion();
            }
        });

        // Listen for success_status changes
        this.api.on('SetValue.cmi.success_status', (_: string, value: string) => {
            if (value === 'passed' || value === 'failed') {
                this.handleCompletion();
            }
        });

        // Auto-commit timer
        if (this.config.autoCommitIntervalMs && this.config.autoCommitIntervalMs > 0) {
            this.commitTimer = setInterval(() => {
                this.handleCommit();
            }, this.config.autoCommitIntervalMs);
        }
    }

    /**
     * Inject the SCORM 2004 API into an iframe's contentWindow.
     * SCORM 2004 expects `window.API_1484_11` on the parent or opener.
     */
    loadIntoFrame(iframe: HTMLIFrameElement): void {
        if (!this.api) {
            throw new Error('Scorm2004Adapter not initialized. Call initialize() first.');
        }

        try {
            const iframeWindow = iframe.contentWindow;
            if (iframeWindow) {
                (iframeWindow as any).API_1484_11 = this.api;
            }
        } catch (e) {
            // Cross-origin restriction — set on current window instead
            (window as any).API_1484_11 = this.api;
        }

        // Also set on current window as fallback
        (window as any).API_1484_11 = this.api;
    }

    /**
     * Get the full CMI data snapshot from scorm-again.
     */
    getCmiData(): Record<string, unknown> {
        if (!this.api) return {};
        try {
            return this.api.renderCMIToJSON() ?? {};
        } catch {
            return {};
        }
    }

    /**
     * Handle CMI commit — send data to backend.
     */
    private async handleCommit(): Promise<void> {
        if (this.destroyed || !this.api) return;

        const cmiData = this.getCmiData();
        try {
            const result: ScormCommitResult = await commitCmi(
                this.config.sessionId,
                this.config.sessionToken,
                'default',
                cmiData,
            );
            this.config.onCommit?.(result);
        } catch (error) {
            this.config.onError?.(error instanceof Error ? error : new Error(String(error)));
        }
    }

    /**
     * Handle completion event from SCORM content.
     */
    private handleCompletion(): void {
        if (this.destroyed || !this.api) return;

        // Commit first, then notify
        this.handleCommit().then(() => {
            const cmi = this.getCmiData() as any;
            const cmiRoot = cmi?.cmi ?? {};
            const score = cmiRoot?.score ?? {};

            const event: ScormCompletionEvent = {
                completionStatus: cmiRoot.completion_status ?? 'unknown',
                successStatus: cmiRoot.success_status ?? 'unknown',
                scoreRaw: score.raw ? parseFloat(score.raw) : null,
                scoreScaled: score.scaled ? parseFloat(score.scaled) : null,
                totalTime: cmiRoot.total_time ?? '',
            };

            this.config.onComplete?.(event);
        });
    }

    /**
     * Clean up — stop auto-commit, remove API references.
     */
    destroy(): void {
        this.destroyed = true;

        if (this.commitTimer) {
            clearInterval(this.commitTimer);
            this.commitTimer = null;
        }

        // Final commit before destroying
        if (this.api) {
            this.handleCommit();
        }

        // Clean up global API reference
        if ((window as any).API_1484_11 === this.api) {
            delete (window as any).API_1484_11;
        }

        this.api = null;
    }
}
